import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ACCEPT_QUESTION_LABEL,
  CONCLUSION_HELPER_PROMPTS,
  CONCLUSION_PROMPT,
  EVIDENCE_QUESTIONS,
  EVIDENCE_SCALE,
  OTHER_FACTOR_GROUPS,
  RISKS_QUESTION_LABEL,
} from '../../constants/adhdTriageOutcomes';
import {
  ADHD_SIGNPOSTING_HINT,
  ADHD_SIGNPOSTING_OPTIONS,
  ADHD_SIGNPOSTING_PROMPT,
} from '../../constants/adhdSignposting';
import { SIGNPOSTING_OTHER_LABEL } from '../../constants/autismSignposting';
import SnapScoreSummary, { buildSnapSnapshot } from './SnapScoreSummary';
import ReferralEvidencePanel from './ReferralEvidencePanel';
import {
  criterionCodeFor,
  decisionTagPayload,
  otherFactorTagsByGroup,
  TAG_KIND,
  tagChipLabel,
  tagsOfKind,
} from '../../constants/evidenceTagging';
import { RmsProviderErrorBoundary } from './RmsProviderErrorBoundary';
import { surfacedItemSources } from '../../utils/otherFactorsSurfacing';
import TriageConfirmDialog from './TriageConfirmDialog';
import { rosterSecondaryText } from '../../utils/providerRoles';
import {
  DECISION_CHECK_GO_BACK,
  DECISION_CHECK_RECORD,
  DECISION_CHECK_TITLE,
  decisionCheckLines,
  decisionCheckSubtitle,
} from '../../utils/triageDecisionCheck';
import {
  EvidenceQuestion,
  PatientProfileStrip,
  RiskStratificationSummary,
  StepProgress,
  TaggedLines,
  ToggleChoice,
  clinicianDisplayName,
  clinicianName,
} from './triageDecisionShared';

/**
 * AdhdTriageDecisionForm
 * ----------------------
 * The ADHD triage decision screens. The autism pathway has its own screens
 * (AutismTriageDecisionForm) built on the DSM-5 criterion structure; the
 * shared presentational pieces live in triageDecisionShared.js.
 *
 * Three screens (TDI-394, Tom King's rulings of 27 October 2026):
 *   1. Evidence — SNAP-IV scores, then the five evidence questions (all
 *      required), each with the lines the clinician tagged to it.
 *   2. Risks and other factors — the lines the clinician tagged as a risk or
 *      an other factor, with their notes; the risks question and detail; one
 *      comment on other factors.
 *   3. Triage decision — the lines the clinician added to their conclusion,
 *      a recap of their answers so far, the conclusion and accept decision
 *      (both required), clinicians involved and signposting.
 *
 * The screens show only what the clinician recorded or tagged in the history
 * review (R2, R-016): no form answers are matched to factors here, untagged
 * factors are not listed or prompted for, and nothing is pre-ticked from a
 * tag (R4). "View referral evidence" still opens every form answer, for the
 * clinician to read and tag by choice.
 *
 * Answers so far survive "Back to proforma": the proforma hands back the last
 * `draft` this form reported through `onDraftChange` when it reopens.
 *
 * Self-contained (no Firebase): on confirm it calls `onConfirm` with the
 * structured decision; wiring to `rmsSubmitTriageDecision` happens at the
 * integration layer (RmsTriageProforma).
 *
 * Props:
 *   - patientSubject?: { sex?, gender?, dob?, age? } — read-only profile strip
 *   - schemas, formInstances: loaded forms (SNAP scores, evidence panel)
 *   - surfacedFactors?: { [factorKey]: [{ label, value, respondent }] } — the
 *     form answers related to each factor. Not shown on any screen; recorded
 *     on the decision as informant information for the letter (R-016).
 *   - riskStratification?, showRiskStratification? — read-only summary
 *   - availableClinicians?, currentClinician? — clinicians involved
 *   - evidenceTags, onAddTag, onRemoveTag — the clinician's own tags
 *   - draft?, onDraftChange? — answers so far, kept by the parent
 *   - combinedReferral? — the referral is triaged on two pathways, so the
 *     check dialog names this one (TDI-693)
 *   - busy?, onConfirm, onCancel?
 *
 * "Complete referral decision" opens the "Check your decision" dialog
 * (TDI-693): nothing is saved until "Record decision", and "Go back" (or Esc)
 * closes it with every answer kept. `onConfirm` may return a promise; the
 * dialog closes once it settles, so a failed save leaves the wizard open with
 * the answers.
 *
 * onConfirm payload (the TD-180 contract):
 *   { pathway: 'adhd', accepted, evidence, otherFactors, conclusion,
 *     signposting, signpostingSelections, signpostingOther, snapSnapshot,
 *     surfacedFactors, evidenceTags, cliniciansInvolved }
 */

// The SNAP-consistency question renders directly under the SNAP score display;
// every other evidence question renders in spec order.
const SNAP_QUESTION_KEY = 'snapConsistency';

// Executes the active step's renderer as a child of the error boundary. Calling
// renderStep() inline in the wizard's own render would throw above the
// boundary (and unmount the whole wizard); routing it through a child
// component keeps the throw below the boundary.
function WizardStepContent({ render }) {
  return render();
}

const STEPS = [
  'Evidence of ADHD symptoms and impairment',
  'Risks and other factors',
  'Triage decision',
];

// Short labels for the horizontal progress stepper.
const STEP_SHORT_LABELS = ['Evidence', 'Risks and other factors', 'Triage decision'];

const EVIDENCE_STEP = 0;
const FACTORS_STEP = 1;
const DECISION_STEP = 2;
const PATHWAY_KEY = 'adhd';

const EVIDENCE_VALUE_LABELS = Object.fromEntries(EVIDENCE_SCALE.map((s) => [s.id, s.label]));

const TEXTAREA_CLASS =
  'block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

export default function AdhdTriageDecisionForm({
  // patientName is still passed by the proforma; the page's shared identity
  // header shows it (TDI-392), so the wizard no longer reads it.
  patientSubject,
  // Loaded form schemas + instances, for the referral-evidence panel.
  schemas = {},
  formInstances,
  surfacedFactors = {},
  riskStratification,
  // Display-only gate for the automated risk-stratification summary. The tier
  // is still computed/stored server-side; this only hides the surfacing. The
  // clinician's own "Risks" question and detail box always render.
  showRiskStratification = true,
  availableClinicians = [],
  // The clinician's own evidence tags on this referral, and the handlers that
  // add and remove them. Owned by the parent so the same tags are shared with
  // the proforma and survive closing the wizard.
  evidenceTags = [],
  onAddTag,
  onRemoveTag,
  // The answers so far from the last time these screens were open, and where
  // to report them as they change.
  draft = null,
  onDraftChange,
  // The clinician's own answers from a decision they reopened to change
  // (TDI-695), in the draft's shape. The screens open with them; answers so
  // far (`draft`) win once the clinician has started again.
  initialAnswers = null,
  currentClinician,
  combinedReferral = false,
  busy = false,
  onConfirm,
  onCancel,
}) {
  const snapQuestion = EVIDENCE_QUESTIONS.find((q) => q.key === SNAP_QUESTION_KEY);
  const otherEvidenceQuestions = EVIDENCE_QUESTIONS.filter((q) => q.key !== SNAP_QUESTION_KEY);
  const seed = draft || initialAnswers;

  const [stepIndex, setStepIndex] = useState(() => seed?.stepIndex ?? 0);

  // Screen 1 — evidence answers: { [questionKey]: { value, comment } }.
  const [evidence, setEvidence] = useState(() => seed?.evidence ?? {});
  // Screen 2 — riskPresent stays null until answered; that drives the nudge.
  const [riskPresent, setRiskPresent] = useState(() => seed?.riskPresent ?? null);
  const [riskDetail, setRiskDetail] = useState(() => seed?.riskDetail ?? '');
  const [otherFactorsComment, setOtherFactorsComment] = useState(
    () => seed?.otherFactorsComment ?? '',
  );
  // Screen 3 — conclusion, decision, clinicians involved and signposting.
  const [conclusion, setConclusion] = useState(() => seed?.conclusion ?? '');
  const [accepted, setAccepted] = useState(() => seed?.accepted ?? null);
  const [involvedIds, setInvolvedIds] = useState(() => new Set(seed?.involvedIds ?? []));
  const [signpostingSelected, setSignpostingSelected] = useState(
    () => seed?.signpostingSelected ?? [],
  );
  const [signpostingOtherOn, setSignpostingOtherOn] = useState(
    () => seed?.signpostingOtherOn ?? false,
  );
  const [signpostingOtherTitle, setSignpostingOtherTitle] = useState(
    () => seed?.signpostingOtherTitle ?? '',
  );
  const [signpostingOtherText, setSignpostingOtherText] = useState(
    () => seed?.signpostingOtherText ?? '',
  );
  const [panelOpen, setPanelOpen] = useState(false);
  // The "Check your decision" dialog (TDI-693).
  const [checkOpen, setCheckOpen] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Report the answers so far to the parent on every change, so going back to
  // the history review and returning picks up exactly here.
  useEffect(() => {
    onDraftChange?.({
      stepIndex,
      evidence,
      riskPresent,
      riskDetail,
      otherFactorsComment,
      conclusion,
      accepted,
      involvedIds: Array.from(involvedIds),
      signpostingSelected,
      signpostingOtherOn,
      signpostingOtherTitle,
      signpostingOtherText,
    });
    // onDraftChange is a fresh closure on every parent render; the answers
    // are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, evidence, riskPresent, riskDetail, otherFactorsComment, conclusion, accepted,
    involvedIds, signpostingSelected, signpostingOtherOn, signpostingOtherTitle,
    signpostingOtherText]);

  const snapSnapshot = useMemo(() => buildSnapSnapshot(formInstances), [formInstances]);

  const setEvidenceValue = (key, value) =>
    setEvidence((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), value } }));
  const setEvidenceComment = (key, comment) =>
    setEvidence((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), comment } }));

  const toggleInvolved = (clinicianId) =>
    setInvolvedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clinicianId)) next.delete(clinicianId);
      else next.add(clinicianId);
      return next;
    });

  // ── Step gating ────────────────────────────────────────────────────────────
  const evidenceComplete = EVIDENCE_QUESTIONS.every((q) => Boolean(evidence[q.key]?.value));
  const conclusionComplete = Boolean(conclusion.trim()) && accepted !== null;
  const risksUntouched = riskPresent === null;

  const canProceed = stepIndex === EVIDENCE_STEP ? evidenceComplete : true;
  const isLastStep = stepIndex === STEPS.length - 1;

  // The wizard renders inside a scrollable full-screen overlay it does not
  // own. After a long step, Next/Back would otherwise leave the user scrolled
  // partway down the new step — always return to the top of the wizard.
  const rootRef = useRef(null);
  useEffect(() => {
    // Optional-call: jsdom has no scrollIntoView.
    rootRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [stepIndex]);

  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));
  const goNext = () => {
    if (!canProceed) return;
    setStepIndex((i) => Math.min(STEPS.length - 1, i + 1));
  };

  const removeTagLabel = (tag) => `Remove ${tagChipLabel(PATHWAY_KEY, tag)} tag — ${tag.label}`;

  // "Complete referral decision" only opens the check; nothing is saved yet.
  const openCheck = () => {
    if (busy || !conclusionComplete || !evidenceComplete) return;
    setCheckOpen(true);
  };

  const signpostingLabels = ADHD_SIGNPOSTING_OPTIONS
    .filter((o) => signpostingSelected.includes(o.optionId))
    .map((o) => o.label);

  // "Record decision": exactly what "Complete referral decision" did before
  // the check existed. The dialog closes once the save settles; a failed
  // save leaves the wizard open with every answer.
  const recordDecision = () => {
    const pending = handleConfirm();
    Promise.resolve(pending).finally(() => {
      if (mountedRef.current) setCheckOpen(false);
    });
  };

  const handleConfirm = () => {
    if (busy || !conclusionComplete || !evidenceComplete) return undefined;

    const evidencePayload = {};
    EVIDENCE_QUESTIONS.forEach((q) => {
      evidencePayload[q.key] = {
        value: evidence[q.key]?.value || '',
        comment: (evidence[q.key]?.comment || '').trim(),
      };
    });

    const cliniciansInvolved = [
      // The decider is always involved and cannot be deselected.
      ...(currentClinician?.clinicianId
        ? [{
            clinicianId: currentClinician.clinicianId,
            clinicianName: clinicianName(currentClinician),
            involved: true,
          }]
        : []),
      ...availableClinicians.map((c) => ({
        clinicianId: c.clinicianId,
        clinicianName: clinicianName(c),
        involved: involvedIds.has(c.clinicianId),
      })),
    ];

    // The form answers related to each factor (TD-185), recorded on the
    // decision as informant information for the letter (R-016). They are not
    // shown on these screens; the letter ranks them below the clinician's own
    // decisions, notes and tags.
    const surfacedFactorsPayload = [];
    OTHER_FACTOR_GROUPS.forEach((group) => {
      group.factors.forEach((factor) => {
        const rows = surfacedFactors[factor.factorKey];
        // A malformed entry is left out rather than failing the decision.
        if (!Array.isArray(rows) || !rows.length) return;
        surfacedFactorsPayload.push({
          factorKey: factor.factorKey,
          factorLabel: factor.label,
          items: rows.map((r) => `${r.label}${r.respondent ? ` (${r.respondent})` : ''}: ${r.value}`),
          ...surfacedItemSources(rows),
        });
      });
    });

    return onConfirm({
      pathway: 'adhd',
      accepted,
      evidence: evidencePayload,
      otherFactors: {
        // The one comment on other factors replaces the per-factor notes and
        // group comments of the earlier screens; support is now tagged.
        comment: otherFactorsComment.trim(),
        risks: {
          // null when the optional risks question was left untouched — the
          // backend records that as "unanswered" rather than an explicit "no".
          presentNeedingReduction: riskPresent,
          detail: riskDetail.trim(),
        },
      },
      conclusion: conclusion.trim(),
      // The pick-list replaced the free-text signposting; the field stays for
      // shape compatibility with older decisions. The server re-sources every
      // selected label from its own list.
      signposting: '',
      signpostingSelections: signpostingSelected.map((optionId) => {
        const option = ADHD_SIGNPOSTING_OPTIONS.find((o) => o.optionId === optionId);
        return { optionId, label: option ? option.label : optionId };
      }),
      signpostingOther: signpostingOtherOn && signpostingOtherTitle.trim()
        ? { title: signpostingOtherTitle.trim(), text: signpostingOtherText.trim() }
        : null,
      snapSnapshot,
      surfacedFactors: surfacedFactorsPayload,
      evidenceTags: evidenceTags.map(decisionTagPayload),
      cliniciansInvolved,
    });
  };

  // ── Step renderers ─────────────────────────────────────────────────────────

  /**
   * The lines the clinician tagged as evidence for this question, shown
   * beneath it so their own reading is in front of them while they grade it.
   * Nothing here grades anything: the rating stays entirely their choice.
   */
  const renderQuestionTags = (question) => (
    <TaggedLines
      heading={`Evidence you tagged as ${criterionCodeFor(PATHWAY_KEY, question.key)}`}
      tags={tagsOfKind(evidenceTags, TAG_KIND.CRITERION).filter((t) => t.criterionKey === question.key)}
      onRemoveTag={onRemoveTag}
      removeLabel={removeTagLabel}
    />
  );

  const renderEvidenceStep = () => (
    <div className="space-y-5">
      <SnapScoreSummary formInstances={formInstances} />
      {snapQuestion && (
        <div>
          <EvidenceQuestion
            question={snapQuestion}
            answer={evidence[snapQuestion.key]}
            onSelect={(value) => setEvidenceValue(snapQuestion.key, value)}
            onComment={(text) => setEvidenceComment(snapQuestion.key, text)}
          />
          {renderQuestionTags(snapQuestion)}
        </div>
      )}
      {otherEvidenceQuestions.map((q) => (
        <div key={q.key}>
          <EvidenceQuestion
            question={q}
            answer={evidence[q.key]}
            onSelect={(value) => setEvidenceValue(q.key, value)}
            onComment={(text) => setEvidenceComment(q.key, text)}
          />
          {renderQuestionTags(q)}
        </div>
      ))}
    </div>
  );

  const renderFactorsStep = () => {
    const factorGroups = otherFactorTagsByGroup(PATHWAY_KEY, evidenceTags);
    return (
      <div className="space-y-5 text-left">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-left">
          <p className="text-sm font-semibold text-gray-900">Risks</p>
          {showRiskStratification && (
            <RiskStratificationSummary riskStratification={riskStratification} />
          )}
          <TaggedLines
            heading="Risks you tagged"
            tags={tagsOfKind(evidenceTags, TAG_KIND.RISK)}
            onRemoveTag={onRemoveTag}
            removeLabel={removeTagLabel}
          />
          <fieldset className="mt-4">
            <legend className="text-sm text-gray-800">{RISKS_QUESTION_LABEL}</legend>
            <div className="mt-2 flex gap-2">
              <ToggleChoice
                name="risksPresent"
                label="Yes"
                checked={riskPresent === true}
                onChange={() => setRiskPresent(true)}
              />
              <ToggleChoice
                name="risksPresent"
                label="No"
                checked={riskPresent === false}
                onChange={() => setRiskPresent(false)}
              />
            </div>
          </fieldset>
          <textarea
            value={riskDetail}
            onChange={(e) => setRiskDetail(e.target.value)}
            rows={3}
            aria-label="Risk detail"
            placeholder="Detail (optional)"
            className={`mt-3 ${TEXTAREA_CLASS}`}
          />
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-left">
          {factorGroups.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-900">Other factors you tagged</p>
              {factorGroups.map(({ group, tags }) => (
                <TaggedLines
                  key={group.id}
                  heading={group.label}
                  tags={tags}
                  onRemoveTag={onRemoveTag}
                  removeLabel={removeTagLabel}
                />
              ))}
            </div>
          )}
          <label className="block text-xs font-medium text-gray-600">
            Comment on other factors (optional)
            <textarea
              value={otherFactorsComment}
              onChange={(e) => setOtherFactorsComment(e.target.value)}
              rows={3}
              className={`mt-1 font-normal ${TEXTAREA_CLASS}`}
            />
          </label>
        </div>
      </div>
    );
  };

  /** A plain recap of the ratings and the risks answer, as recorded. */
  const renderAnswersSoFar = () => (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-left">
      <p className="text-sm font-semibold text-gray-900">Your answers so far</p>
      <ul className="mt-2 space-y-1 text-xs text-gray-800" aria-label="Your answers so far">
        {EVIDENCE_QUESTIONS.map((q) => (
          <li key={q.key}>
            <span className="font-medium">{criterionCodeFor(PATHWAY_KEY, q.key)}:</span>{' '}
            {EVIDENCE_VALUE_LABELS[evidence[q.key]?.value] || 'Not answered'}
          </li>
        ))}
        <li>
          <span className="font-medium">Risks to reduce first:</span>{' '}
          {riskPresent === true ? 'Yes' : riskPresent === false ? 'No' : 'Not answered'}
        </li>
      </ul>
    </div>
  );

  const renderSignposting = () => (
    <div className="space-y-4 text-left">
      <div>
        <p className="text-base font-medium text-gray-900">{ADHD_SIGNPOSTING_PROMPT}</p>
        <p className="mt-0.5 text-xs text-gray-500">{ADHD_SIGNPOSTING_HINT}</p>
        <div className="mt-3 space-y-2" role="group" aria-label="Signposting services">
          {ADHD_SIGNPOSTING_OPTIONS.map((option) => (
            <label
              key={option.optionId}
              className="flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-gray-50"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={signpostingSelected.includes(option.optionId)}
                onChange={(e) => setSignpostingSelected((prev) => (
                  e.target.checked
                    ? [...prev, option.optionId]
                    : prev.filter((id) => id !== option.optionId)
                ))}
              />
              <span>{option.label}</span>
            </label>
          ))}
          <label className="flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={signpostingOtherOn}
              onChange={(e) => setSignpostingOtherOn(e.target.checked)}
            />
            <span>{SIGNPOSTING_OTHER_LABEL}</span>
          </label>
          {signpostingOtherOn && (
            <div className="ml-6 space-y-2">
              <input
                type="text"
                value={signpostingOtherTitle}
                onChange={(e) => setSignpostingOtherTitle(e.target.value)}
                aria-label="Other service name"
                placeholder="Service or support name"
                className={TEXTAREA_CLASS}
              />
              <textarea
                value={signpostingOtherText}
                onChange={(e) => setSignpostingOtherText(e.target.value)}
                rows={3}
                aria-label="Other service details"
                placeholder="What the family should know about it"
                className={TEXTAREA_CLASS}
              />
            </div>
          )}
        </div>
      </div>
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
        <p className="text-xs text-blue-800">
          Each selected service appears in the letter under its own heading, with the
          service description from the Options4CYP further-help document.
        </p>
      </div>
    </div>
  );

  const renderDecisionStep = () => (
    <div className="space-y-5 text-left">
      {renderAnswersSoFar()}
      <div>
        <p className="text-base font-medium text-gray-900">{CONCLUSION_PROMPT}</p>
        <ul className="mt-2 ml-4 list-disc text-xs text-gray-500">
          {CONCLUSION_HELPER_PROMPTS.map((prompt) => (
            <li key={prompt}>{prompt}</li>
          ))}
        </ul>
        {/* What the clinician added to their conclusion while reading sits
            beside the box they write it in. */}
        <TaggedLines
          heading="Added to your conclusion"
          tags={tagsOfKind(evidenceTags, TAG_KIND.CONCLUSION)}
          onRemoveTag={onRemoveTag}
          removeLabel={removeTagLabel}
        />
        <textarea
          value={conclusion}
          onChange={(e) => setConclusion(e.target.value)}
          rows={6}
          aria-label="Your conclusion"
          className={`mt-3 ${TEXTAREA_CLASS}`}
        />
      </div>

      <fieldset>
        <legend className="text-base font-medium text-gray-900">{ACCEPT_QUESTION_LABEL}</legend>
        <div className="mt-2 flex gap-2">
          <ToggleChoice
            name="acceptDecision"
            label="Yes"
            checked={accepted === true}
            onChange={() => setAccepted(true)}
          />
          <ToggleChoice
            name="acceptDecision"
            label="No"
            checked={accepted === false}
            onChange={() => setAccepted(false)}
          />
        </div>
      </fieldset>

      <div>
        <p className="text-base font-medium text-gray-900">Clinicians involved</p>
        <p className="mt-0.5 text-xs text-gray-500">
          ADHD triage is a joint decision made by the team. You are recorded as recording
          it; mark the other clinicians who were part of this decision.
        </p>
        <ul className="mt-2 space-y-2">
          {currentClinician?.clinicianId && (
            <li className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <span className="text-sm text-gray-900">
                {clinicianDisplayName(currentClinician)}{' '}
                <span className="text-xs text-gray-500">(you — recording this decision)</span>
              </span>
              <button
                type="button"
                disabled
                aria-label={`${clinicianDisplayName(currentClinician)} involved (locked)`}
                className="cursor-not-allowed rounded-md bg-primary px-3 py-1 text-xs font-medium text-white opacity-80"
              >
                Involved
              </button>
            </li>
          )}
          {availableClinicians.map((c) => {
            const involved = involvedIds.has(c.clinicianId);
            const name = clinicianDisplayName(c);
            // TDI-703: the job title (else the email) tells two people with
            // the same name, or one person's two accounts, apart.
            const secondary = rosterSecondaryText(c);
            return (
              <li
                key={c.clinicianId}
                className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2"
              >
                <span className="text-sm text-gray-900">
                  {name}
                  {secondary && <span className="text-xs text-gray-500"> · {secondary}</span>}
                </span>
                <div className="flex gap-1" role="group" aria-label={`${name} involvement`}>
                  <button
                    type="button"
                    onClick={() => !involved && toggleInvolved(c.clinicianId)}
                    aria-pressed={involved}
                    className={`rounded-md px-3 py-1 text-xs font-medium ${
                      involved
                        ? 'bg-primary text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Involved
                  </button>
                  <button
                    type="button"
                    onClick={() => involved && toggleInvolved(c.clinicianId)}
                    aria-pressed={!involved}
                    className={`rounded-md px-3 py-1 text-xs font-medium ${
                      !involved
                        ? 'bg-gray-600 text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Not involved
                  </button>
                </div>
              </li>
            );
          })}
          {availableClinicians.length === 0 && (
            <li className="text-xs text-gray-500">
              No other ADHD clinicians are registered for this organisation.
            </li>
          )}
        </ul>
      </div>

      {renderSignposting()}
    </div>
  );

  const renderStep = () => {
    if (stepIndex === EVIDENCE_STEP) return renderEvidenceStep();
    if (stepIndex === FACTORS_STEP) return renderFactorsStep();
    if (stepIndex === DECISION_STEP) return renderDecisionStep();
    return null;
  };

  return (
    <div ref={rootRef} className="mx-auto flex max-w-3xl flex-col gap-6 text-left">
      <div>
        <StepProgress labels={STEP_SHORT_LABELS} current={stepIndex} />
        <div className="mt-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              Step {stepIndex + 1} of {STEPS.length}
            </p>
            <h3 className="mt-0.5 text-xl font-semibold text-gray-900">{STEPS[stepIndex]}</h3>
            {/* The child's identity sits in the page's shared header on every
                step (TDI-392), so it is not repeated here. */}
          </div>
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="whitespace-nowrap rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            View referral evidence
          </button>
        </div>
      </div>

      {/* Patient context strip, kept visible while weighing risks and factors. */}
      {stepIndex === FACTORS_STEP && <PatientProfileStrip subject={patientSubject} />}

      {/* The boundary sits below the wizard's state, so a crash while rendering
          one step cannot unmount the wizard or discard the answers on other
          steps; keying by step lets Back/Next leave a failed step normally. */}
      <div className="min-h-[220px]">
        <RmsProviderErrorBoundary inline key={stepIndex}>
          <WizardStepContent render={renderStep} />
        </RmsProviderErrorBoundary>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={stepIndex === 0 ? onCancel : goBack}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          {stepIndex === 0 ? 'Cancel' : 'Back'}
        </button>
        <div className="flex items-center gap-3">
          {stepIndex === FACTORS_STEP && risksUntouched && (
            <p className="text-xs text-amber-700">
              The risks question has not been answered — you can still continue.
            </p>
          )}
          {isLastStep ? (
            <button
              type="button"
              onClick={openCheck}
              disabled={busy || !conclusionComplete}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                !busy && conclusionComplete
                  ? 'bg-primary hover:bg-primary/90'
                  : 'cursor-not-allowed bg-gray-300'
              }`}
            >
              Complete referral decision
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              disabled={!canProceed}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                canProceed ? 'bg-primary hover:bg-primary/90' : 'cursor-not-allowed bg-gray-300'
              }`}
            >
              Next
            </button>
          )}
        </div>
      </div>

      <TriageConfirmDialog
        open={checkOpen}
        onOpenChange={setCheckOpen}
        title={DECISION_CHECK_TITLE}
        subtitle={decisionCheckSubtitle(PATHWAY_KEY, combinedReferral)}
        lines={decisionCheckLines({
          pathway: PATHWAY_KEY,
          accepted: accepted === true,
          signpostingLabels,
          signpostingOtherTitle: signpostingOtherOn ? signpostingOtherTitle : '',
          // The decision can be changed until the letter is signed off (TDI-695).
          showChangeLine: true,
        })}
        cancelLabel={DECISION_CHECK_GO_BACK}
        confirmLabel={DECISION_CHECK_RECORD}
        busy={busy}
        onConfirm={recordDecision}
      />

      <ReferralEvidencePanel
        pathway="adhd"
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        schemas={schemas}
        formInstances={formInstances}
        tags={evidenceTags}
        onAddTag={onAddTag}
        onRemoveTag={onRemoveTag}
      />
    </div>
  );
}
